/**
 * Cellular Automata Cave Generator
 * Produces organic, cave-like layouts via random fill + smoothing.
 */

import type { GridMap, GeneratorConfig } from '../types';
import { createGrid, addEntity, coordKey } from '../grid';
import { SeededRandom } from '../random';

export function generateCave(config: GeneratorConfig): GridMap {
  const { width, height, seed } = config;
  const fillProb = config.fillProbability ?? 0.45;
  const iterations = config.smoothIterations ?? 5;
  const rng = new SeededRandom(seed);

  const map = createGrid(width, height, 'wall');
  map.seed = seed;
  map.name = 'Cave';

  // Step 1: Random fill (leave 1-cell border as walls)
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      map.cells[y][x].terrain = rng.bool(1 - fillProb) ? 'floor' : 'wall';
    }
  }

  // Step 2: Cellular automata smoothing
  for (let i = 0; i < iterations; i++) {
    const snapshot = map.cells.map(row => row.map(c => c.terrain));

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let wallCount = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            if (snapshot[y + dy]?.[x + dx] === 'wall') wallCount++;
          }
        }
        // Standard 4-5 rule
        map.cells[y][x].terrain = wallCount >= 5 ? 'wall' : 'floor';
      }
    }
  }

  // Step 3: Flood-fill to find largest connected floor region
  const visited = new Set<string>();
  let largestRegion: string[] = [];

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const key = coordKey(x, y);
      if (visited.has(key) || map.cells[y][x].terrain !== 'floor') continue;

      const region = floodFill(map, x, y, visited);
      if (region.length > largestRegion.length) {
        largestRegion = region;
      }
    }
  }

  // Fill all non-largest-region floor cells with wall
  const largestSet = new Set(largestRegion);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      if (map.cells[y][x].terrain === 'floor' && !largestSet.has(coordKey(x, y))) {
        map.cells[y][x].terrain = 'wall';
      }
    }
  }

  // Step 4: Place player near center of largest region
  if (largestRegion.length > 0) {
    // Find the floor cell closest to map center
    const cx = width / 2;
    const cy = height / 2;
    let bestKey = largestRegion[0];
    let bestDist = Infinity;

    for (const key of largestRegion) {
      const [kx, ky] = key.split(',').map(Number);
      const d = (kx - cx) ** 2 + (ky - cy) ** 2;
      if (d < bestDist) {
        bestDist = d;
        bestKey = key;
      }
    }

    const [px, py] = bestKey.split(',').map(Number);
    return addEntity(map, {
      id: 'player',
      x: px, y: py,
      type: 'player',
      name: 'Player',
      lightRadius: 12,
    });
  }

  return map;
}

function floodFill(map: GridMap, startX: number, startY: number, visited: Set<string>): string[] {
  const region: string[] = [];
  const stack = [{ x: startX, y: startY }];

  while (stack.length > 0) {
    const { x, y } = stack.pop()!;
    const key = coordKey(x, y);
    if (visited.has(key)) continue;
    if (x < 0 || x >= map.width || y < 0 || y >= map.height) continue;
    if (map.cells[y][x].terrain !== 'floor') continue;

    visited.add(key);
    region.push(key);

    stack.push({ x: x + 1, y }, { x: x - 1, y }, { x, y: y + 1 }, { x, y: y - 1 });
  }

  return region;
}
